import React, {
    useCallback, useEffect, useRef, useState,
} from 'react'
import {
    Dialog, DialogFooter, DialogType, PrimaryButton, TextField,
} from '@fluentui/react'
import Candidacy, { ErrorCandidacy } from 'requests/objects/candidacy'
import CancelRequestError from 'requests/errors/cancelRequestError'
import UnauthorizedError from 'requests/errors/unauthorizedError'
import InvalidEntityError from 'requests/errors/invalidEntityError'
import NotImplementedError from 'requests/errors/notImplementedError'
import CandidaciesHandler from 'requests/handlers/candidaciesHandler'
import Status from 'types/status'
import HuntsHandler from 'requests/handlers/huntsHandler'
import Hunt, { ErrorHunt } from 'requests/objects/hunt'
import Time from 'helpers/methods/time'
import { isValidDate } from 'helpers/methods/common'

/**
 * BoardSetInterviewDateModal
 * @param {object} props Props
 * @param {Candidacy | Hunt} props.item item
 * @param {CandidaciesHandler | HuntsHandler} props.handler handler
 * @param {boolean} props.isVisible isVisible
 * @param {(isVisible: boolean) => void} props.setIsVisible setIsVisible
 * @param {(item: Candidacy | Hunt) => void} props.onUpdate onUpdate
 * @returns {JSX.Element} Returns
 */
export default function BoardSetInterviewClientInfoModal({
    item: itemProps, handler, isVisible, setIsVisible, onUpdate,
}) {
    const [item, setItem] = useState(itemProps || new Candidacy())
    const [status, setStatus] = useState(Status.IDLE)
    const [errorField, setErrorField] = useState(/** @type {ErrorHunt | ErrorCandidacy} */({}))

    /** @type {React.Ref<import('requests/apiHandler').RequestApi<Candidacy | Hunt>>} */
    const handlerUpdateBoardItem = useRef(null)

    const update = useCallback(async () => {
        try {
            setStatus(Status.PENDING)
            handlerUpdateBoardItem.current = handler.patchInterviewClientInfo(
                /** @type {Candidacy} */(item).candidacyId || /** @type {Hunt} */(item).huntId,
                item,
            )
            const newItem = await handlerUpdateBoardItem.current.fetch()
            setStatus(Status.RESOLVED)
            setIsVisible(false)
            onUpdate(newItem)
        } catch (error) {
            switch (error?.constructor) {
                case CancelRequestError:
                case UnauthorizedError:
                case NotImplementedError:
                    break
                case InvalidEntityError:
                    setErrorField(/** @type {InvalidEntityError<ErrorHunt | ErrorCandidacy>} */(error).errorField)
                    // eslint-disable-next-line no-console
                    console.error(error)
                    setStatus(Status.REJECTED)
                    break
                default:
                    // eslint-disable-next-line no-console
                    console.error(error)
                    setStatus(Status.REJECTED)
                    break
            }
        }
    }, [item, handler, setIsVisible, onUpdate])

    // Update local item on props change
    useEffect(() => {
        setItem(itemProps || new Candidacy())
        setErrorField(/** @type {ErrorHunt | ErrorCandidacy} */({}))
    }, [itemProps])

    // Cancel request on dismount
    useEffect(() => () => {
        handlerUpdateBoardItem?.current?.cancel()
    }, [])

    return (
        <Dialog
            hidden={!isVisible}
            onDismiss={() => setIsVisible(false)}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: 'Planifier l\'entretien client',
                // eslint-disable-next-line max-len
                subText: 'Veuillez sélectionner les informations nécessaires pour planifier l\'entretien  client',
            }}
            modalProps={{
                isBlocking: true,
            }}
            maxWidth="555px"
        >
            <form
                onSubmit={ev => {
                    ev.preventDefault()
                    update()
                }}
            >
                <TextField
                    label="Date et heure du début"
                    value={item.interviewClientStartDate && isValidDate(item.interviewClientStartDate)
                        ? Time(item.interviewClientStartDate).getLocaleDateTimeString()
                        : ''}
                    readOnly={status === Status.PENDING}
                    borderless={status === Status.PENDING}
                    onChange={(_ev, newVal) => {
                        const newInterviewStartDateinterviewClientStartDate = new Date(newVal)
                        setItem(prev => ({
                            ...prev,
                            interviewClientStartDate: new Date(newVal),
                            // eslint-disable-next-line max-len
                            interviewClientEndDate: new Date(`${newVal.slice(0, 10)}T${(newInterviewStartDateinterviewClientStartDate.getHours() + 1).toString().padStart(2, '0')}:${newInterviewStartDateinterviewClientStartDate.getMinutes().toString().padStart(2, '0')}`),
                        }))
                    }}
                    errorMessage={errorField.interviewClientStartDate}
                    type="datetime-local"
                    required
                />
                <br />
                <TextField
                    label="Heure de fin"
                    value={item.interviewClientEndDate && isValidDate(item.interviewClientEndDate)
                        ? Time(item.interviewClientEndDate).getLocaleTimeString()
                        : ''}
                    readOnly={status === Status.PENDING}
                    borderless={status === Status.PENDING}
                    onChange={(_ev, newVal) => {
                        setItem(prev => ({
                            ...prev,
                            interviewClientEndDate: new Date(`${Time(prev.interviewClientStartDate).getLocaleDateString()}T${newVal}`),
                        }))
                    }}
                    errorMessage={errorField.interviewClientEndDate}
                    type="time"
                    required
                    disabled={!item.interviewClientStartDate}
                />
                <br />
                <DialogFooter>
                    <PrimaryButton
                        type="submit"
                        text="Enregistrer"
                        disabled={status === Status.PENDING}
                    />
                </DialogFooter>
            </form>
        </Dialog>
    )
}
